import type {
  IdentityManifestHistoryItem,
  IdentityReport,
} from "./identityWorkbenchContracts";

export const MAX_MATRIX_SUBJECTS = 12;
export const MAX_TIMELINE_TRACKLETS = 64;

export type MatrixRelation =
  | "self"
  | "cannot_link"
  | "must_link"
  | "constraint"
  | "not_recorded";

export type MatrixCell = {
  relation: MatrixRelation;
  reasons: string[];
  decisionId: string | null;
};

export type PairwiseMatrix = {
  subjectIds: string[];
  cells: Map<string, MatrixCell>;
  truncated: boolean;
};

function pairKey(left: string, right: string) {
  return [left, right].sort().join("\0");
}

export function pseudonymousSubjectLabel(
  subjects: readonly { echo_identity_id: string }[],
  identityId: string,
) {
  const ordinal = [...subjects]
    .sort((left, right) =>
      left.echo_identity_id.localeCompare(right.echo_identity_id),
    )
    .findIndex((item) => item.echo_identity_id === identityId);
  return ordinal >= 0
    ? `Subject ${String(ordinal + 1).padStart(2, "0")}`
    : "Subject";
}

export function buildPairwiseMatrix(report: IdentityReport): PairwiseMatrix {
  const allSubjectIds = report.subjects
    .map((item) => item.echo_identity_id)
    .sort();
  const subjectIds = allSubjectIds.slice(0, MAX_MATRIX_SUBJECTS);
  const visible = new Set(subjectIds);
  const cells = new Map<string, MatrixCell>();
  for (const decision of report.decisions) {
    const ids = decision.subject_ids.filter((item) => visible.has(item));
    if (ids.length < 2) continue;
    for (let left = 0; left < ids.length; left += 1) {
      for (let right = left + 1; right < ids.length; right += 1) {
        const reasons = decision.reasons;
        const relation: MatrixRelation = reasons.some((reason) =>
          reason.includes("cannot_link"),
        )
          ? "cannot_link"
          : reasons.some((reason) => reason.includes("must_link"))
            ? "must_link"
            : "constraint";
        const key = pairKey(ids[left], ids[right]);
        const prior = cells.get(key);
        if (prior?.relation === "cannot_link") continue;
        cells.set(key, {
          relation,
          reasons,
          decisionId: decision.decision_id,
        });
      }
    }
  }
  return {
    subjectIds,
    cells,
    truncated: allSubjectIds.length > subjectIds.length,
  };
}

export function matrixCell(
  matrix: PairwiseMatrix,
  left: string,
  right: string,
): MatrixCell {
  if (left === right) {
    return { relation: "self", reasons: [], decisionId: null };
  }
  return (
    matrix.cells.get(pairKey(left, right)) ?? {
      relation: "not_recorded",
      reasons: [],
      decisionId: null,
    }
  );
}

export type TimelineTracklet = IdentityReport["tracklets"][number] & {
  identityId: string | null;
  leftPercent: number;
  widthPercent: number;
};

export function buildTimelineTracklets(
  report: IdentityReport,
  identityId: string | null,
) {
  const identityByTracklet = new Map<string, string>();
  for (const assignment of report.assignments) {
    if (!assignment.echo_identity_id) continue;
    for (const trackletId of assignment.tracklet_ids) {
      identityByTracklet.set(trackletId, assignment.echo_identity_id);
    }
  }
  const candidates = report.tracklets
    .filter(
      (tracklet) =>
        identityId == null ||
        identityByTracklet.get(tracklet.tracklet_id) === identityId,
    )
    .sort(
      (left, right) =>
        left.source_media_id.localeCompare(right.source_media_id) ||
        left.start_us - right.start_us,
    );
  const maximumByMedia = new Map<string, number>();
  for (const item of candidates) {
    maximumByMedia.set(
      item.source_media_id,
      Math.max(maximumByMedia.get(item.source_media_id) ?? 0, item.end_us),
    );
  }
  const visible = candidates.slice(0, MAX_TIMELINE_TRACKLETS);
  const tracklets: TimelineTracklet[] = visible.map((item) => {
    const maximum = Math.max(1, maximumByMedia.get(item.source_media_id) ?? 1);
    return {
      ...item,
      identityId: identityByTracklet.get(item.tracklet_id) ?? null,
      leftPercent: (item.start_us / maximum) * 100,
      widthPercent: Math.max(
        1,
        ((item.end_us - item.start_us) / maximum) * 100,
      ),
    };
  });
  return { tracklets, truncated: candidates.length > visible.length };
}

export function selectedManifest(
  manifests: readonly IdentityManifestHistoryItem[],
  version: number | null,
) {
  return (
    manifests.find((item) => item.manifest_version === version) ??
    [...manifests].sort(
      (left, right) =>
        (right.manifest_version ?? 0) - (left.manifest_version ?? 0),
    )[0] ??
    null
  );
}

export function identityHintWarnings(
  manifest: IdentityManifestHistoryItem | null,
  hints: { identityId: string | null; manifestVersion: number | null },
) {
  const warnings: string[] = [];
  if (
    hints.manifestVersion != null &&
    manifest?.manifest_version !== hints.manifestVersion
  ) {
    warnings.push(
      "The linked manifest version is not in the authorized history.",
    );
  }
  if (
    hints.identityId != null &&
    !manifest?.subjects.some(
      (subject) => subject.echo_identity_id === hints.identityId,
    )
  ) {
    warnings.push(
      "The linked identity does not belong to the authorized manifest.",
    );
  }
  return warnings;
}

export function formatPpm(value: number | null) {
  return value == null ? "Not recorded" : `${(value / 10_000).toFixed(1)}%`;
}
